import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Check, Tag } from "lucide-react";
import { chiNotify } from "@/components/ChiNotification";
import { trpc } from "@/lib/trpc";

type Promotion = {
  enabled: boolean;
  unitAmountCents: number;
  currency: string;
  startsAt: Date | string | null;
  endsAt: Date | string;
};

type Settings = {
  promotion: Promotion | null;
  regularPriceCents: number | null;
  currency: string;
  pricingMode: string | null;
  serverNow: string;
};

type DiscountDraft = {
  enabled: boolean;
  amount: string;
  startsAt: string;
  endsAt: string;
};

const fieldStyle: React.CSSProperties = {
  width: "100%",
  minHeight: 44,
  padding: ".6rem .72rem",
  border: "1px solid rgba(255,255,255,.15)",
  borderRadius: ".55rem",
  background: "rgba(255,255,255,.055)",
  color: "#F1F5F9",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: ".86rem",
  boxSizing: "border-box",
  colorScheme: "dark",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  marginBottom: ".4rem",
  color: "#A7B3C5",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: ".68rem",
  fontWeight: 700,
  letterSpacing: ".07em",
  textTransform: "uppercase",
};

const actionStyle: React.CSSProperties = {
  minHeight: 44,
  padding: ".55rem .85rem",
  border: "1px solid rgba(0,212,255,.35)",
  borderRadius: ".55rem",
  background: "rgba(0,212,255,.1)",
  color: "#7DEAFF",
  fontFamily: "'Space Grotesk', sans-serif",
  fontSize: ".78rem",
  fontWeight: 700,
  cursor: "pointer",
};

export function dollarsToCents(value: string): number | null {
  const trimmed = value.trim();
  if (!/^(?:\d+)(?:\.\d{1,2})?$/.test(trimmed)) return null;
  const [whole, fraction = ""] = trimmed.split(".");
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, "0"));
  return Number.isSafeInteger(cents) ? cents : null;
}

function localInputValue(date: Date | string | null | undefined): string {
  if (!date) return "";
  const parsed = date instanceof Date ? date : new Date(date);
  if (!Number.isFinite(parsed.getTime())) return "";
  const local = new Date(parsed.getTime() - parsed.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

function draftFromSettings(settings: Settings | undefined): DiscountDraft {
  const promotion = settings?.promotion;
  return {
    enabled: Boolean(promotion?.enabled),
    amount: promotion ? (promotion.unitAmountCents / 100).toFixed(2) : "",
    startsAt: localInputValue(promotion?.startsAt),
    endsAt: localInputValue(promotion?.endsAt),
  };
}

function promotionStatus(settings: Settings | undefined): string {
  const promotion = settings?.promotion;
  if (!promotion?.enabled) return "Off";
  const now = new Date(settings?.serverNow ?? Date.now());
  const start = promotion.startsAt ? new Date(promotion.startsAt) : null;
  const end = new Date(promotion.endsAt);
  if (start && now < start) return "Scheduled";
  if (now >= end) return "Expired";
  return "Active";
}

export default function EventTicketDiscountControl({
  eventSlug,
  readOnly = false,
  isSpecialEvent = false,
}: {
  eventSlug: string;
  readOnly?: boolean;
  isSpecialEvent?: boolean;
}) {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const settingsQuery = trpc.ticketPromotions.settings.useQuery(
    { eventSlug },
    {
      retry: false,
      refetchInterval: 30_000,
      refetchOnWindowFocus: true,
    },
  );
  const settings = settingsQuery.data as Settings | undefined;
  const [draft, setDraft] = useState<DiscountDraft>(() => draftFromSettings(undefined));
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const savePromotion = trpc.ticketPromotions.save.useMutation({
    onSuccess: async () => {
      setError(null);
      await Promise.all([
        utils.ticketPromotions.settings.invalidate({ eventSlug }),
        utils.events.publicPricingPolicy.invalidate({ eventSlug }),
        queryClient.invalidateQueries({ queryKey: ["homepage-ticket-quote", eventSlug] }),
      ]);
      setDirty(false);
      chiNotify.success("Ticket discount saved", {
        description: "Your discount window was saved. Ticket checkout settings are unchanged.",
      });
    },
    onError: mutationError => {
      const message = mutationError.message || "The ticket discount could not be saved.";
      setError(message);
      chiNotify.error("Ticket discount not saved", { description: message });
    },
  });

  useEffect(() => {
    if (settings && !dirty) setDraft(draftFromSettings(settings));
  }, [settings, dirty]);

  const timeZone = useMemo(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || "local time",
    [],
  );
  const regularPrice = settings?.regularPriceCents;
  const status = promotionStatus(settings);
  const locked = readOnly || isSpecialEvent;

  function update<K extends keyof DiscountDraft>(key: K, value: DiscountDraft[K]) {
    setDraft(current => ({ ...current, [key]: value }));
    setDirty(true);
    setError(null);
  }

  function save(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (locked || !dirty) return;
    const input: {
      eventSlug: string;
      enabled: boolean;
      unitAmountCents?: number;
      startsAt?: string;
      endsAt?: string;
    } = { eventSlug, enabled: draft.enabled };

    if (draft.enabled) {
      const cents = dollarsToCents(draft.amount);
      if (cents === null || cents < 50) {
        setError("Enter a discounted ticket price of at least 0.50, with up to two decimal places.");
        return;
      }
      if (regularPrice === null || regularPrice === undefined || cents >= regularPrice) {
        setError("The discounted ticket price must be lower than the regular ticket price.");
        return;
      }
      const startDate = new Date(draft.startsAt);
      const endDate = new Date(draft.endsAt);
      if (!draft.startsAt || !Number.isFinite(startDate.getTime()) || !draft.endsAt || !Number.isFinite(endDate.getTime())) {
        setError("Choose both a start and end date and time.");
        return;
      }
      if (startDate.getTime() >= endDate.getTime()) {
        setError("The end date and time must be after the start.");
        return;
      }
      input.unitAmountCents = cents;
      input.startsAt = startDate.toISOString();
      input.endsAt = endDate.toISOString();
    }
    setError(null);
    savePromotion.mutate(input);
  }

  function discard() {
    setDraft(draftFromSettings(settings));
    setDirty(false);
    setError(null);
  }

  return (
    <section
      aria-labelledby="event-ticket-discount-heading"
      style={{
        marginTop: ".8rem",
        padding: ".95rem 1rem",
        border: "1px solid rgba(245,166,35,.2)",
        borderRadius: ".65rem",
        background: "rgba(245,166,35,.045)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: ".5rem", flexWrap: "wrap" }}>
        <Tag size={15} aria-hidden="true" style={{ color: "#FCD34D" }} />
        <h3 id="event-ticket-discount-heading" style={{ margin: 0, color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".72rem", fontWeight: 750, letterSpacing: ".09em", textTransform: "uppercase" }}>
          Timeboxed ticket discount
        </h3>
        <span aria-live="polite" style={{ marginLeft: "auto", padding: ".25rem .55rem", border: "1px solid rgba(255,255,255,.14)", borderRadius: 999, color: status === "Active" ? "#86EFAC" : "#CBD5E1", background: "rgba(255,255,255,.04)", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".68rem", fontWeight: 700 }}>
          {status}
        </span>
      </div>

      {settingsQuery.isLoading ? (
        <div aria-label="Loading discount settings" style={{ display: "grid", gap: ".55rem", marginTop: ".85rem" }}>
          <div style={{ height: 14, width: "52%", borderRadius: 8, background: "rgba(255,255,255,.09)" }} />
          <div style={{ height: 44, borderRadius: 8, background: "rgba(255,255,255,.06)" }} />
          <div style={{ height: 44, borderRadius: 8, background: "rgba(255,255,255,.06)" }} />
        </div>
      ) : settingsQuery.isError ? (
        <div style={{ marginTop: ".8rem" }}>
          <p role="alert" style={{ margin: "0 0 .6rem", color: "#FDA4AF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".78rem" }}>Discount settings could not be loaded.</p>
          <button type="button" style={actionStyle} onClick={() => void settingsQuery.refetch()}>Retry</button>
        </div>
      ) : (
        <form onSubmit={save} style={{ marginTop: ".8rem" }}>
          {isSpecialEvent && !readOnly && (
            <p role="status" style={{ margin: "0 0 .75rem", color: "#FCD34D", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".75rem", lineHeight: 1.5 }}>
              Ticket discounts are unavailable for special events that require manual approval.
            </p>
          )}
          <label style={{ display: "flex", alignItems: "center", gap: ".65rem", minHeight: 44, color: "#E2E8F0", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".83rem", fontWeight: 650, cursor: locked ? "default" : "pointer" }}>
            <input
              type="checkbox"
              checked={draft.enabled}
              disabled={locked || settingsQuery.isLoading}
              onChange={event => update("enabled", event.target.checked)}
              style={{ width: 20, height: 20, accentColor: "#F5A623" }}
            />
            Enable discount during the selected window
          </label>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 190px), 1fr))", gap: ".7rem", marginTop: ".65rem" }}>
            <label>
              <span style={labelStyle}>Discounted ticket price ({settings?.currency?.toUpperCase() ?? "USD"})</span>
              <input
                aria-label="Discounted ticket price"
                type="number"
                inputMode="decimal"
                min="0.01"
                step="0.01"
                placeholder="Enter a price"
                value={draft.amount}
                disabled={locked || !draft.enabled}
                onChange={event => update("amount", event.target.value)}
                style={fieldStyle}
              />
              <span style={{ display: "block", marginTop: ".35rem", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".71rem" }}>
                Regular price: {regularPrice == null ? "Unavailable" : new Intl.NumberFormat(undefined, { style: "currency", currency: settings?.currency || "USD" }).format(regularPrice / 100)}
              </span>
            </label>
            <label>
              <span style={labelStyle}>Starts</span>
              <input aria-label="Discount starts" type="datetime-local" value={draft.startsAt} disabled={locked || !draft.enabled} onChange={event => update("startsAt", event.target.value)} style={fieldStyle} />
            </label>
            <label>
              <span style={labelStyle}>Ends</span>
              <input aria-label="Discount ends" type="datetime-local" value={draft.endsAt} disabled={locked || !draft.enabled} onChange={event => update("endsAt", event.target.value)} style={fieldStyle} />
            </label>
          </div>
          <p style={{ margin: ".55rem 0 0", display: "flex", alignItems: "flex-start", gap: ".35rem", color: "#94A3B8", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".7rem", lineHeight: 1.5 }}>
            <CalendarClock size={13} aria-hidden="true" style={{ flex: "0 0 auto", marginTop: ".12rem" }} />
            <span>Times shown in {timeZone}; saved as UTC. At the end time, the regular ticket price resumes automatically. This changes price policy only and never enables ticket checkout.</span>
          </p>
          {error && <p role="alert" style={{ margin: ".6rem 0 0", color: "#FDA4AF", fontFamily: "'Space Grotesk', sans-serif", fontSize: ".76rem", lineHeight: 1.45 }}>{error}</p>}
          {!locked && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: ".55rem", flexWrap: "wrap", marginTop: ".8rem" }}>
              {dirty && <button type="button" onClick={discard} style={{ ...actionStyle, borderColor: "rgba(255,255,255,.18)", background: "rgba(255,255,255,.04)", color: "#CBD5E1" }}>Discard changes</button>}
              <button type="submit" disabled={!dirty || savePromotion.isPending} style={{ ...actionStyle, opacity: !dirty || savePromotion.isPending ? .55 : 1, cursor: !dirty || savePromotion.isPending ? "not-allowed" : "pointer" }}>
                {savePromotion.isPending ? "Saving…" : <><Check size={14} style={{ verticalAlign: "middle", marginRight: ".3rem" }} />Save discount</>}
              </button>
            </div>
          )}
        </form>
      )}
    </section>
  );
}
